import 'draft-js/dist/Draft.css';
import './Decorators/Decorators.css';
import React from 'react';
import { EditorState, Editor, getDefaultKeyBinding, EditorCommand, DraftHandleValue, RichUtils } from 'draft-js';
import { IFormInputProps } from '../../interfaces/IFormInputProps';
import { IFormInputComponent } from '../../interfaces/IFormInputComponent';
import { RichTextValue, customStyleMap, isMacOS, maxTabDepth } from './common';
import { useResizeObject } from '../../../utils/effects';
import { RichtextEditorCommands, HotKey } from './commands';

type RichEditorProps = IFormInputProps<RichTextValue, HTMLTextAreaElement> & {
    placeholder?: string;
    editorState: EditorState;
    keyBindingFn?: (e: React.KeyboardEvent<{}>) => EditorCommand | null | undefined,
    openLinkEditor?: () => void,
    onResize?: (offsetWidth: number, offsetHeight: number) => void,
    onEditorStateChange?: (editorState: EditorState) => void,
    spellCheck?: boolean,
    useKeyTab?: boolean,
}

const RichEditor = React.forwardRef<IFormInputComponent, RichEditorProps>((props, ref) => {
    const domEditor = React.useRef<Editor | null>(null);

    const readonly = React.useMemo(() => !!props.disabled || !!props.readOnly, [props.disabled, props.readOnly]);
    const focus = React.useCallback(() => setTimeout(() => domEditor.current?.focus(), 100), [domEditor, readonly]);
    React.useImperativeHandle(ref, (): IFormInputComponent => ({ focus: () => focus() }));

    const handleKeyCommand = React.useCallback((command: EditorCommand, state: EditorState): DraftHandleValue => {
        const cmd = Object.values(RichtextEditorCommands).find(_ => _.style?.toLocaleLowerCase() === command.toLocaleLowerCase());
        if (!cmd?.onCommand || readonly) {
            return 'not-handled';
        }

        if (command === RichtextEditorCommands.link.style) {
            props.openLinkEditor?.();
        } else {
            onChange(cmd.onCommand(state))
        }
        return 'handled';
    }, [readonly, props.editorState]);

    const keyBindingFn = React.useCallback((e: React.KeyboardEvent<{}>): EditorCommand | null => {
        if (readonly) {
            return null;
        }
        
        const externalResult = props.keyBindingFn?.(e);
        if (externalResult !== undefined) {
            return externalResult;
        }

        const cmd = Object.values(RichtextEditorCommands).find(_ => isHotKeyPressed(_.hotKey, e));

        return cmd?.style ?? getDefaultKeyBinding(e);
    }, [readonly, props.keyBindingFn]);

    const onChange = React.useCallback((state?: EditorState) => {
        if (readonly || !state) {
            return;
        }

        props.onEditorStateChange?.(state);
    }, [readonly, props.editorState, props.onEditorStateChange]);

    const refEditor = useResizeObject<HTMLDivElement>((offsetWidth: number, offsetHeight: number) => props.onResize?.(offsetWidth, offsetHeight));
    
    const onTab = React.useCallback((e: React.KeyboardEvent<{}>) => {
        e.preventDefault();

        const newEditorState = RichUtils.onTab(e, props.editorState, maxTabDepth);
        if (props.editorState === newEditorState) {
            return;
        }

        onChange(newEditorState);
    }, [props.useKeyTab, onChange, props.editorState]);

    return <div ref={refEditor} className='editor' >
        <Editor
            ref={domEditor}
            readOnly={readonly}
            placeholder={props.placeholder}
            editorState={props.editorState}
            customStyleMap={customStyleMap}
            handleKeyCommand={handleKeyCommand}
            keyBindingFn={keyBindingFn}
            onChange={onChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            preserveSelectionOnBlur
            spellCheck={props.spellCheck}
            onTab={props.useKeyTab ? onTab : undefined}
        />
    </div>;
})

export default RichEditor;

const isHotKeyPressed = (hotKey: HotKey | undefined, event: React.KeyboardEvent<{}>): boolean => {
    if (!hotKey || !event) {
        return false;
    }

    return (event.keyCode ? event.keyCode === hotKey.keyCode : event.key === hotKey.key)
        && (isMacOS()
            ? (event.altKey === !!hotKey.macos.option && event.metaKey === !!hotKey.macos.cmd)
            : (event.altKey === !!hotKey.win.alt && event.ctrlKey === !!hotKey.win.ctrl)
        );
}